import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, Printer, CheckCircle2 } from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Constants & small helpers                                                   */
/* -------------------------------------------------------------------------- */

const NOT_ENABLED = "Not Enabled";

const CHECK_STATUS_COLOR = {
  Passed: "text-green-600",
  Failed: "text-red-600",
  [NOT_ENABLED]: "text-orange-500",
};

const isUnset = (v) => v === null || v === undefined;
const orNA = (v) => v || "N/A";
const validityText = (valid) => (valid ? "Valid" : "Invalid");

// Spec section 17: clean -> "AI Confidence Score" (or "Integrity Score" for
// artifacts), otherwise "Risk Score".
function getScoreLabel(isCleanResult, isArtifact) {
  if (!isCleanResult) return "Risk Score";
  return isArtifact ? "Integrity Score" : "AI Confidence Score";
}

// The TSA timestamp comes back as raw ISO 8601 UTC (e.g. "2026-08-27T06:32:49Z").
// Render it in the user's local time, human-readable.
function formatTimestamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

function checkStatusText(passed) {
  if (isUnset(passed)) return NOT_ENABLED;
  return passed ? "Passed" : "Failed";
}

// Artifact verification can report "Not Enabled" for optional checks; the
// document flow has always treated a missing value as a failure.
function optionalStatus(value, allowNotEnabled, passText, failText) {
  if (allowNotEnabled && isUnset(value)) return NOT_ENABLED;
  return value ? passText : failText;
}

/* -------------------------------------------------------------------------- */
/* Data builders                                                               */
/* -------------------------------------------------------------------------- */

function buildChecks({ verificationResult, noun, isArtifact, classification, aiScore }) {
  const aiPassed = classification ? classification === "clean" : aiScore > 80;

  return [
    { name: `${noun} Integrity`, passed: verificationResult.merkle_valid },
    {
      name: isArtifact ? "Signature Validation" : "PQ Signature Validation",
      passed: verificationResult.pq_signature_valid,
    },
    { name: "Certificate Validation", passed: verificationResult.certificate_valid },
    { name: "Timestamp Validation", passed: verificationResult.tsa_valid },
    { name: "Blockchain Validation", passed: verificationResult.blockchain_valid },
    { name: isArtifact ? "Artifact Integrity Analysis" : "AI Tamper Detection", passed: aiPassed },
  ];
}

function buildDetails({ verificationResult: vr, noun, isArtifact }) {
  return [
    { label: `${noun} Hash`, value: orNA(vr.cid) },
    { label: "IPFS CID", value: orNA(vr.original_ipfs_cid) },
    { label: "Signature Type", value: orNA(vr.algorithm || vr.sign_mode) },
    ...(isArtifact ? [{ label: "Trust Level", value: orNA(vr.trust_level) }] : []),
    { label: "Timestamp", value: orNA(formatTimestamp(vr.timestamp)) },
    { label: "Merkle Root", value: orNA(vr.stored_merkle_root) },
    { label: `${noun} Valid`, value: validityText(vr.document_valid) },
    {
      label: "Blockchain Status",
      value: optionalStatus(vr.blockchain_valid, isArtifact, "Verified", "Failed"),
    },
    {
      label: "Certificate Status",
      value: optionalStatus(vr.certificate_valid, isArtifact, "Valid", "Invalid"),
    },
    {
      label: "Timestamp Status",
      value: optionalStatus(vr.tsa_valid, isArtifact, "Valid", "Invalid"),
    },
  ];
}

function buildSummaryItems({ verificationResult: vr, noun, file, scoreLabel, scoreValue, verificationTime }) {
  return [
    { label: "Overall Status", value: validityText(vr.document_valid), badge: true },
    { label: scoreLabel, value: `${scoreValue}%` },
    { label: "Verification Time", value: verificationTime },
    { label: `${noun} Name`, value: orNA(file?.name) },
    { label: "File Size", value: orNA(file?.size) },
    { label: "Signed By", value: orNA(vr.certificate_subject) },
    { label: "Signature Type", value: vr.algorithm || vr.sign_mode || "Unknown" },
  ];
}

function checkLine(check) {
  return `${check.passed ? "✓" : "✗"} ${check.name} — ${checkStatusText(check.passed)}`;
}

function buildReportText({ noun, file, scoreLabel, scoreValue, verificationTime, verificationResult, checks, details }) {
  return [
    "VERIFICATION REPORT",
    "===================",
    `${noun}: ${orNA(file?.name)}`,
    `File Size: ${orNA(file?.size)}`,
    `${scoreLabel}: ${scoreValue}%`,
    `Verification Time: ${verificationTime}`,
    `Overall Status: ${validityText(verificationResult.document_valid)}`,
    "",
    "VERIFICATION CHECKS",
    ...checks.map(checkLine),
    "",
    "DETAILS",
    ...details.map((d) => `${d.label}: ${d.value}`),
  ].join("\n");
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function SummaryRow({ item }) {
  const badgeClass = item.value === "Valid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";

  return (
    <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white">
      <p className="text-[16px] text-gray-500">{item.label}</p>
      {item.badge ? (
        <span className={`px-3 py-1 rounded-lg text-[14px] font-semibold ${badgeClass}`}>
          {item.value}
        </span>
      ) : (
        <p className="text-[16px]  text-gray-800 text-right break-all max-w-[200px]">{item.value}</p>
      )}
    </div>
  );
}

function SummaryCard({ items }) {
  return (
    <div className="bg-gray-50 border rounded-2xl p-6">
      <h3 className="text-[20px] font-semibold text-gray-700 mb-4">Summary</h3>
      <div className="space-y-3">
        {items.map((item) => (
          <SummaryRow key={item.label} item={item} />
        ))}
      </div>
    </div>
  );
}

function CheckRow({ check }) {
  const status = checkStatusText(check.passed);

  return (
    <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white">
      <div className="flex items-center gap-3">
        <CheckCircle2 size={18} className="text-green-500" />
        <p className="text-[16px]  text-gray-800">{check.name}</p>
      </div>
      <span className={`font-semibold text-[14px] ${CHECK_STATUS_COLOR[status]}`}>{status}</span>
    </div>
  );
}

function ChecksCard({ checks }) {
  return (
    <div className="bg-gray-50 border rounded-2xl p-6">
      <h3 className="text-[20px] font-semibold text-gray-700 mb-4">Verification Checks</h3>
      <div className="space-y-3">
        {checks.map((check) => (
          <CheckRow key={check.name} check={check} />
        ))}
      </div>
    </div>
  );
}

function DetailsCard({ details }) {
  return (
    <div className="bg-gray-50 border rounded-2xl p-3 sm:p-5 md:p-6 mb-6">
      <h3 className="text-[20px] text-gray-700 mb-4">Details</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {details.map((item) => (
          <div
            key={item.label}
            className="flex items-start justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white"
          >
            <p className="text-[16px] text-gray-400 w-36 flex-shrink-0">{item.label}</p>
            <p className="text-[16px] font-semibold text-gray-800 text-right break-all">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function VerificationReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const verificationResult = location.state?.verificationResult || {};
  const uploadedFiles = location.state?.uploadedFiles || [];
  const aiScore = location.state?.aiScore || 0;
  const riskScore = location.state?.riskScore ?? 0;
  const classification = location.state?.classification;

  // Artifact verification reuses this wizard; the adapter sets kind: "artifact".
  const isArtifact = verificationResult.kind === "artifact";
  const noun = isArtifact ? "Artifact" : "Document";

  const isCleanResult = classification ? classification === "clean" : true;
  const scoreLabel = getScoreLabel(isCleanResult, isArtifact);
  const scoreValue = isCleanResult ? aiScore : riskScore;
  const file = uploadedFiles[0];
  const verificationTime = new Date().toLocaleString();

  const checks = buildChecks({ verificationResult, noun, isArtifact, classification, aiScore });
  const details = buildDetails({ verificationResult, noun, isArtifact });
  const summaryItems = buildSummaryItems({
    verificationResult,
    noun,
    file,
    scoreLabel,
    scoreValue,
    verificationTime,
  });

  const handleDownload = () => {
    const text = buildReportText({
      noun,
      file,
      scoreLabel,
      scoreValue,
      verificationTime,
      verificationResult,
      checks,
      details,
    });
    downloadTextFile(`verification_report_${file?.name || "document"}.txt`, text);
  };

  return (
    <div className="w-full bg-white mx-auto">
      {/* HEADER */}
      <div className="flex justify-end gap-3 mb-4">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition text-14px font-medium"
        >
          <Download size={16} /> Download PDF
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 border border-gray-300 px-5 py-2.5 rounded-xl hover:bg-gray-50 transition text-14px font-medium text-gray-700"
        >
          <Printer size={16} /> Print
        </button>
      </div>

      {/* TOP CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <SummaryCard items={summaryItems} />
        <ChecksCard checks={checks} />
      </div>

      <DetailsCard details={details} />

      {/* FOOTER */}
      <div className="flex justify-between mt-3">
        <button
          onClick={() => navigate("/verification-result")}
          className="inline-flex items-center gap-1.5 h-[44px] px-4 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-14px font-medium"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}